import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IIssue } from '@d-orca/shared/trusstor';

/**
 * Form-mode dialogs (create + edit) live in Redux because they own transient
 * form state and don't need to be deep-linkable.
 *
 * The issue HUB is *not* a Redux mode — it's URL-driven (see useOpenIssue).
 * That gives us deep links, back/forward, and one-way data flow with no
 * URL↔Redux sync loop to maintain.
 */
export type DialogMode = 'create' | 'edit';

/** The D-ORCA task a new issue is raised against: the id is sent, the title
 *  is only shown. */
export type LinkedTask = { id: string; title: string };

export type DialogState = {
  mode: DialogMode | null;
  issueId: string | null;
  prefill: (Partial<IIssue> & { taskId?: string }) | null;
  linkedTask: LinkedTask | null;
};

const initialState: DialogState = {
  mode: null,
  issueId: null,
  prefill: null,
  linkedTask: null,
};

const slice = createSlice({
  name: 'dialog',
  initialState,
  reducers: {
    openEditDialog(state, action: PayloadAction<string>) {
      state.mode = 'edit';
      state.issueId = action.payload;
      state.prefill = null;
      state.linkedTask = null;
    },
    openCreateDialog: {
      reducer(state, action: PayloadAction<Partial<IIssue> | null>) {
        state.mode = 'create';
        state.issueId = null;
        state.prefill = action.payload;
        state.linkedTask = null;
      },
      prepare(prefill?: Partial<IIssue>) {
        return { payload: prefill ?? null };
      },
    },
    /** Create an issue raised against a committed task. */
    openCreateDialogForTask(state, action: PayloadAction<LinkedTask>) {
      state.mode = 'create';
      state.issueId = null;
      state.prefill = { taskId: action.payload.id };
      state.linkedTask = action.payload;
    },
    closeDialog(state) {
      state.mode = null;
      state.issueId = null;
      state.prefill = null;
      state.linkedTask = null;
    },
  },
});

export const { openEditDialog, openCreateDialog, openCreateDialogForTask, closeDialog } = slice.actions;
export const dialogReducer = slice.reducer;
